import React, { useState, useEffect, useContext } from 'react';
import cn from 'classnames';
import { withStyles } from '@material-ui/styles';
import { ClientContext, ClientContextProvider } from './ClientContext';
import queryString from 'query-string';
import ChevronLeft from '@material-ui/icons/ChevronLeft';
import Button from '@material-ui/core/Button';
import { AppContext } from '../../AppContext';
import roles from '../../helpers/roles';

// SERVICES
import { getOrderById } from '../../services';

// TABS
import { TabBar } from '../TabBar';
import { ClientDetailTab, ClientInstructionsTab, FitCredentialsTab } from './tabs';
import AgentMasterList from '../AgentMasterList';
import AutomatedCallConfigurationTab from '../CallManager/AutomatedCallConfigurationTab';

const { ADMIN, QA, UNDERWRITER, UNDERWRITER_READONLY } = roles;

const styles = theme => ({
  blue: {
    backgroundColor: 'blue',
  },

  clientLogo: {
    height: '75%',
    marginLeft: 20,
    maxWidth: 200,
    maxHeight: 50,
  },
});

export const CLIENT_DETAIL_TAB_KEY = 'CLIENT_DETAIL_TAB';
export const tabKeys = {
  details: 'details',
  instructions: 'instructions',
};

const ClientTabsContent = withStyles(styles)(props => {
  const { classes, setPageTitle, pageMode } = props;
  const { clientId } = props.match.params;

  const {
    state: { roles },
  } = useContext(AppContext);
  const { dispatch } = useContext(ClientContext);

  const [query] = useState(queryString.parse(props.history.location.search));
  const [activeTab, setActiveTab] = useState(0);
  const [allowedTabs, setAllowedTabs] = useState([]);
  const [tabsLoaded, setTabsLoaded] = useState({
    details: false,
    instructions: false,
  });

  const updateLoaded = (tabKey, value) => {
    setTabsLoaded(Object.assign({}, tabsLoaded, { [tabKey]: value }));
  };

  const updateActiveTab = key => {
    localStorage.setItem(CLIENT_DETAIL_TAB_KEY, key);
    const activeTabIndex = allowedTabs.findIndex(tab => tab.key === key);
    setActiveTab(activeTabIndex);
  };

  const tabs = [
    {
      label: 'Details',
      key: 'details',
      permissions: [ADMIN],
      component: (
        <ClientDetailTab
          clientId={clientId}
          loaded={tabsLoaded.details}
          updateLoaded={val => updateLoaded('details', val)}
          query={query}
        />
      ),
    },
    {
      label: 'Special Instructions',
      key: 'specialInstructions',
      permissions: [ADMIN],
      component: (
        <ClientInstructionsTab
          clientId={clientId}
          loaded={tabsLoaded.communication}
          updateLoaded={val => updateLoaded('instructions', val)}
          query={query}
        />
      ),
    },
    {
      label: 'Agent Master List',
      key: 'agentMasterList',
      permissions: [ADMIN],
      component: <AgentMasterList clientId={clientId} />,
    },
    {
      label: 'Automated Call Configuration',
      key: 'automatedCallConfiguration',
      permissions: [ADMIN],
      component: <AutomatedCallConfigurationTab clientId={clientId} />,
    },
    {
      label: 'FIT Credentials',
      key: 'fitCredentials',
      permissions: [ADMIN],
      component: <FitCredentialsTab clientId={clientId} />,
    },
  ];

  const checkLastFocusedTabKey = () => {
    const lastFocusedTab = localStorage.getItem(CLIENT_DETAIL_TAB_KEY);

    if (lastFocusedTab) {
      updateActiveTab(lastFocusedTab);
    } else {
      setActiveTab(0); // set to the first tab on the page
    }
  };

  // whenever the allowed tabs change (when the page loads and the roles are brought in), check what the last focused tab was
  useEffect(() => {
    checkLastFocusedTabKey();
  }, [allowedTabs]);

  useEffect(() => {
    setAllowedTabs(tabs.filter(tab => tab.permissions.some(permission => roles.indexOf(permission) >= 0)));
  }, [roles]);

  useEffect(() => {
    return () => {
      localStorage.removeItem(CLIENT_DETAIL_TAB_KEY);
    };
  }, []);

  return (
    <div className={cn('flex-1', 'col-center-start')}>
      {query.referrer && (
        <Button style={{ marginBottom: 10 }} onClick={() => props.history.goBack()}>
          <ChevronLeft />
          Return to {query.referrer}
        </Button>
      )}
      <TabBar tabs={allowedTabs} changeTab={updateActiveTab} activeTab={activeTab} />

      <div
        className={cn(
          ![tabs.communication].includes(activeTab) && 'flex-1',
          'align-stretch',
          'grid-container',
          'my-10',
          // 'card',
          // 'border-left-green',
        )}
        style={{ marginLeft: 0, marginRight: 0 }}
      >
        {allowedTabs[activeTab] && allowedTabs[activeTab].component}
      </div>
    </div>
  );
});

export const ClientTabs = props => (
  <ClientContextProvider>
    <ClientTabsContent {...props} />
  </ClientContextProvider>
);
